import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from 'helpers/axios';
import { errorMessage } from 'helpers/common';
import * as Sentry from "@sentry/react";
import { normalizeModule } from 'helpers/module';

const EMPTY_ARRAY = Object.freeze([]);
const GLOBAL_PAYFACTOR_CACHE_KEY = '__global__';
const buildGlobalValueCacheKey = () => `${localStorage.getItem('period') || 'current'}::${localStorage.getItem('module') || 'current'}`;
const buildScopedCacheKey = (payFactorHashId, moduleKey = null) => `${payFactorHashId || 'unknown'}::${moduleKey ?? 'all'}`;
const buildValueCacheKey = (payFactorHashId, moduleKey = null, all = false) => {
  if (all) return `${payFactorHashId || 'unknown'}::all`;
  if (moduleKey !== null && moduleKey !== undefined) return `${payFactorHashId || 'unknown'}::${moduleKey}`;
  return `${payFactorHashId || 'unknown'}::current`;
};
const PAYFACTOR_REQUEST_IDLE = 'idle';
const PAYFACTOR_REQUEST_PENDING = 'pending';
const PAYFACTOR_REQUEST_SUCCEEDED = 'succeeded';
const PAYFACTOR_REQUEST_FAILED = 'failed';
const GLOBAL_PAYFACTOR_REQUEST_IDLE = 'idle';
const GLOBAL_PAYFACTOR_REQUEST_PENDING = 'pending';
const GLOBAL_PAYFACTOR_REQUEST_SUCCEEDED = 'succeeded';
const GLOBAL_PAYFACTOR_REQUEST_FAILED = 'failed';

const clearPayfactorValueCache = (state, payFactorHashId) => {
  if (!payFactorHashId) return;

  Object.keys(state.valuesByKey).forEach((cacheKey) => {
    if (cacheKey.startsWith(`${payFactorHashId}::`)) {
      delete state.valuesByKey[cacheKey];
    }
  });

  Object.keys(state.valueRequestStatusByKey).forEach((cacheKey) => {
    if (cacheKey.startsWith(`${payFactorHashId}::`)) {
      delete state.valueRequestStatusByKey[cacheKey];
    }
  });
};

const normalizeArrayPayload = (payload) => {
  const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : EMPTY_ARRAY);

  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const module = normalizeModule(row.module ?? row.mode_scope, null);

    return {
      ...row,
      module,
      module_key: row.module_key || module,
    };
  });
};

const normalizeGroupMessages = (payload) => {
  if (Array.isArray(payload?.meta?.group_messages)) return payload.meta.group_messages;
  return EMPTY_ARRAY;
};

const fetchPayfactors = createAsyncThunk('payfactors/fetchPayfactors', async (_, { rejectWithValue }) => {
  try {
    const { data, status } = await api.get('/pay-factors');
    if (status === 200) return data;
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch pay factors'));
  }
});

const fetchActivePayfactor = createAsyncThunk('payfactors/fetchActivePayfactor', async (_, { rejectWithValue }) => {
  try {
    const { data, status } = await api.get('/pay-factors/current');
    if (status === 200) return data;
  } catch (err) {
    if (err?.response?.status !== 404) {
      Sentry.captureException(err);
    }
    return rejectWithValue(errorMessage(err, 'fetch current pay factor'));
  }
});

const fetchPayfactorValues = createAsyncThunk(
  'payfactors/fetchPayfactorValues',
  async ({ payFactorHashId, moduleKey = null, all = false }, { rejectWithValue }) => {
    try {
      const params = {};
      if (all) {
        params.all = true;
      }

      if (moduleKey !== null && moduleKey !== undefined) {
        params.module = moduleKey;
      }

      const { data, status } = await api.get(`/pay-factors/${payFactorHashId}/values`, { params });
      if (status === 200) {
        return {
          payFactorHashId,
          moduleKey,
          all,
          data: normalizeArrayPayload(data),
        };
      }
    } catch (err) {
      Sentry.captureException(err);
      return rejectWithValue(errorMessage(err, 'fetch pay factor values'));
    }
  },
  {
    condition: ({ payFactorHashId, moduleKey = null, all = false, force = false }, { getState }) => {
      if (!payFactorHashId) return false;
      if (force) return true;

      const cacheKey = buildValueCacheKey(payFactorHashId, moduleKey, all);
      const payfactorsState = getState().payfactors;
      const requestStatus = payfactorsState.valueRequestStatusByKey?.[cacheKey] || PAYFACTOR_REQUEST_IDLE;

      if (requestStatus === PAYFACTOR_REQUEST_PENDING) {
        return false;
      }

      return true;
    },
  }
);

const fetchCurrentPayfactorValues = createAsyncThunk('payfactors/fetchCurrentPayfactorValues', async (moduleKey = null, { dispatch, getState, rejectWithValue }) => {
  try {
    let current = getState().payfactors.activePayfactor;

    if (!current?.hash_id) {
      current = await dispatch(fetchActivePayfactor()).unwrap();
    }

    if (!current?.hash_id) {
      return {
        payFactorHashId: null,
        moduleKey,
        data: [],
      };
    }

    const resultAction = await dispatch(fetchPayfactorValues({
      payFactorHashId: current.hash_id,
      moduleKey,
        all: false,
    }));

    if (fetchPayfactorValues.fulfilled.match(resultAction)) {
      return resultAction.payload;
    }

    if (resultAction.meta?.condition) {
      return {
        payFactorHashId: current.hash_id,
        moduleKey,
        all: false,
        data: getState().payfactors.valuesByKey[buildValueCacheKey(current.hash_id, moduleKey, false)] || EMPTY_ARRAY,
      };
    }

    throw resultAction.payload || resultAction.error;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'fetch current pay factor values'));
  }
});

const fetchPayfactorMappings = createAsyncThunk('payfactors/fetchPayfactorMappings', async ({ payFactorHashId }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.get('/pay-factors/mappings', {
      params: {
        pay_factor_hash_id: payFactorHashId,
      },
    });

    if (status === 200) {
      return {
        payFactorHashId,
        data: normalizeArrayPayload(data),
        groupMessages: normalizeGroupMessages(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch pay factor mappings'));
  }
});

const fetchGlobalPayfactorValues = createAsyncThunk('payfactors/fetchGlobalPayfactorValues', async (_, { rejectWithValue }) => {
  const cacheKey = buildGlobalValueCacheKey();
  try {
    const { data, status } = await api.get('/pay-factors/values');
    if (status === 200) {
      return {
        cacheKey,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch global pay factor values'));
  }
}, {
  condition: ({ force = false } = {}, { getState }) => {
    if (force) return true;

    const cacheKey = buildGlobalValueCacheKey();
    const status = getState().payfactors.globalValuesRequestStatusByKey?.[cacheKey] || GLOBAL_PAYFACTOR_REQUEST_IDLE;
    return ![GLOBAL_PAYFACTOR_REQUEST_PENDING, GLOBAL_PAYFACTOR_REQUEST_SUCCEEDED].includes(status);
  },
});

const fetchGlobalPayfactorMappings = createAsyncThunk('payfactors/fetchGlobalPayfactorMappings', async ({ moduleKey = null } = {}, { rejectWithValue }) => {
  try {
    const params = {};
    if (moduleKey !== null && moduleKey !== undefined) {
      params.module = moduleKey;
    }

    const { data, status } = await api.get('/pay-factors/mappings', { params });

    if (status === 200) {
      return {
        data: normalizeArrayPayload(data),
        groupMessages: normalizeGroupMessages(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch global pay factor mappings'));
  }
});

const fetchGroupSelections = createAsyncThunk('payfactors/fetchGroupSelections', async ({ payFactorHashId, moduleKey = null }, { rejectWithValue }) => {
  try {
    const params = {
      pay_factor_hash_id: payFactorHashId,
    };

    if (moduleKey !== null && moduleKey !== undefined) {
      params.module = moduleKey;
    }

    const { data, status } = await api.get('/pay-factors/group-selections', { params });
    if (status === 200) {
      return {
        payFactorHashId,
        moduleKey,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch pay factor group selections'));
  }
});

const updateGroupSelections = createAsyncThunk('payfactors/updateGroupSelections', async ({ payFactorHashId, moduleKey, selections }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.put('/pay-factors/group-selections', {
      pay_factor_hash_id: payFactorHashId,
      module: moduleKey,
      selections,
    });

    if (status === 200) {
      return {
        payFactorHashId,
        moduleKey,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'update pay factor group selections'));
  }
});

const fetchGlobalGroupSelections = createAsyncThunk('payfactors/fetchGlobalGroupSelections', async ({ moduleKey = null } = {}, { rejectWithValue }) => {
  try {
    const params = {};
    if (moduleKey !== null && moduleKey !== undefined) {
      params.module = moduleKey;
    }

    const { data, status } = await api.get('/pay-factors/group-selections', { params });
    if (status === 200) {
      return {
        moduleKey,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch global pay factor group selections'));
  }
});

const updateGlobalGroupSelections = createAsyncThunk('payfactors/updateGlobalGroupSelections', async ({ moduleKey, selections = null, prefixes = null }, { rejectWithValue }) => {
  try {
    const payload = {
      module: moduleKey,
    };

    if (Array.isArray(selections) && selections.length > 0) {
      payload.selections = selections;
    }

    if (Array.isArray(prefixes) && prefixes.length > 0) {
      payload.prefixes = prefixes;
    }

    const { data, status } = await api.put('/pay-factors/group-selections', payload);

    if (status === 200) {
      return {
        moduleKey,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'update global pay factor group selections'));
  }
});

const fetchGlobalGradeSelections = createAsyncThunk('payfactors/fetchGlobalGradeSelections', async ({ moduleKey = null, groupCode = null } = {}, { rejectWithValue }) => {
  try {
    const params = {};
    if (moduleKey !== null && moduleKey !== undefined) {
      params.module = moduleKey;
    }

    if (groupCode) {
      params.group_code = groupCode;
    }

    const { data, status } = await api.get('/pay-factors/grade-selections', { params });
    if (status === 200) {
      return {
        moduleKey,
        groupCode,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch global pay factor grade selections'));
  }
});

const updateGlobalGradeSelections = createAsyncThunk('payfactors/updateGlobalGradeSelections', async ({ moduleKey, groupCode, selections }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.put('/pay-factors/grade-selections', {
      module: moduleKey,
      group_code: groupCode,
      selections,
    });

    if (status === 200) {
      return {
        moduleKey,
        groupCode,
        data: normalizeArrayPayload(data),
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'update global pay factor grade selections'));
  }
});

export const payfactorSlice = createSlice({
  name: 'payfactors',
  initialState: {
    payfactors: null,
    activePayfactor: null,
    valuesByKey: {},
    valueRequestStatusByKey: {},
    mappingsByHash: {},
    mappingGroupMessagesByHash: {},
    groupSelectionsByKey: {},
    globalValuesByKey: {},
    globalValuesRequestStatusByKey: {},
    globalMappings: EMPTY_ARRAY,
    globalMappingGroupMessages: EMPTY_ARRAY,
    globalGroupSelections: EMPTY_ARRAY,
    globalGradeSelections: EMPTY_ARRAY,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayfactors.fulfilled, (state, action) => {
        state.payfactors = action.payload.data;
      })
      .addCase(fetchActivePayfactor.fulfilled, (state, action) => {
        state.activePayfactor = action.payload;
      })
      .addCase(fetchActivePayfactor.rejected, (state, action) => {
        if (action.payload?.statusCode === 404) {
          state.activePayfactor = null;
        }
      })
      .addCase(fetchPayfactorValues.pending, (state, action) => {
        const { payFactorHashId, moduleKey = null, all = false } = action.meta.arg;
        state.valueRequestStatusByKey[buildValueCacheKey(payFactorHashId, moduleKey, all)] = PAYFACTOR_REQUEST_PENDING;
      })
      .addCase(fetchPayfactorValues.fulfilled, (state, action) => {
        const { payFactorHashId, moduleKey, all = false, data } = action.payload;
        const cacheKey = buildValueCacheKey(payFactorHashId, moduleKey, all);
        state.valuesByKey[cacheKey] = data;
        state.valueRequestStatusByKey[cacheKey] = PAYFACTOR_REQUEST_SUCCEEDED;
      })
      .addCase(fetchPayfactorValues.rejected, (state, action) => {
        const { payFactorHashId, moduleKey = null, all = false } = action.meta.arg || {};
        if (!payFactorHashId) return;
        state.valueRequestStatusByKey[buildValueCacheKey(payFactorHashId, moduleKey, all)] = PAYFACTOR_REQUEST_FAILED;
      })
      .addCase(fetchPayfactorMappings.fulfilled, (state, action) => {
        state.mappingsByHash[action.payload.payFactorHashId] = action.payload.data;
        state.mappingGroupMessagesByHash[action.payload.payFactorHashId] = action.payload.groupMessages;
      })
      .addCase(fetchGlobalPayfactorValues.pending, (state) => {
        state.globalValuesRequestStatusByKey[buildGlobalValueCacheKey()] = GLOBAL_PAYFACTOR_REQUEST_PENDING;
      })
      .addCase(fetchGlobalPayfactorValues.fulfilled, (state, action) => {
        state.globalValuesByKey[action.payload.cacheKey] = action.payload.data;
        state.globalValuesRequestStatusByKey[action.payload.cacheKey] = GLOBAL_PAYFACTOR_REQUEST_SUCCEEDED;
        state.valuesByKey[buildValueCacheKey(GLOBAL_PAYFACTOR_CACHE_KEY, null, true)] = action.payload.data;
      })
      .addCase(fetchGlobalPayfactorValues.rejected, (state, action) => {
        if (!action.meta?.condition) {
          state.globalValuesRequestStatusByKey[buildGlobalValueCacheKey()] = GLOBAL_PAYFACTOR_REQUEST_FAILED;
        }
      })
      .addCase(fetchGlobalPayfactorMappings.fulfilled, (state, action) => {
        state.globalMappings = action.payload.data;
        state.globalMappingGroupMessages = action.payload.groupMessages;
        state.mappingsByHash[GLOBAL_PAYFACTOR_CACHE_KEY] = action.payload.data;
        state.mappingGroupMessagesByHash[GLOBAL_PAYFACTOR_CACHE_KEY] = action.payload.groupMessages;
      })
      .addCase(fetchGroupSelections.fulfilled, (state, action) => {
        const { payFactorHashId, moduleKey, data } = action.payload;
        state.groupSelectionsByKey[buildScopedCacheKey(payFactorHashId, moduleKey)] = data;
      })
      .addCase(updateGroupSelections.fulfilled, (state, action) => {
        const { payFactorHashId, moduleKey, data } = action.payload;
        state.groupSelectionsByKey[buildScopedCacheKey(payFactorHashId, moduleKey)] = data;
        clearPayfactorValueCache(state, payFactorHashId);
      })
      .addCase(fetchGlobalGroupSelections.fulfilled, (state, action) => {
        state.globalGroupSelections = action.payload.data;
        state.groupSelectionsByKey[buildScopedCacheKey(GLOBAL_PAYFACTOR_CACHE_KEY, action.payload.moduleKey)] = action.payload.data;
      })
      .addCase(updateGlobalGroupSelections.fulfilled, (state, action) => {
        state.globalGroupSelections = action.payload.data;
        state.groupSelectionsByKey[buildScopedCacheKey(GLOBAL_PAYFACTOR_CACHE_KEY, action.payload.moduleKey)] = action.payload.data;
      })
      .addCase(fetchGlobalGradeSelections.fulfilled, (state, action) => {
        state.globalGradeSelections = action.payload.data;
      })
      .addCase(updateGlobalGradeSelections.fulfilled, (state, action) => {
        const nextRows = action.payload.data || EMPTY_ARRAY;
        const targetModuleKey = normalizeModule(action.payload.moduleKey, null);
        const targetGroupCode = action.payload.groupCode;

        state.globalGradeSelections = [
          ...(state.globalGradeSelections || []).filter((row) => !(
            normalizeModule(row?.module, null) === targetModuleKey &&
            String(row?.group_code || '') === String(targetGroupCode || '')
          )),
          ...nextRows,
        ];
      });
  }
});

export const getPayfactors = (state) => state.payfactors.payfactors;
export const getActivePayfactor = (state) => state.payfactors.activePayfactor;
export const getPayfactorValuesCache = (state) => state.payfactors.valuesByKey;

export const selectPayfactorValues = (state, payFactorHashId, moduleKey = null, options = {}) => {
  if (!payFactorHashId) return EMPTY_ARRAY;
  return state.payfactors.valuesByKey[buildValueCacheKey(payFactorHashId, moduleKey, options.all === true)] || EMPTY_ARRAY;
};

export const selectCurrentPayfactorValues = (state) => {
  const payFactorHashId = state.payfactors.activePayfactor?.hash_id;
  if (!payFactorHashId) return EMPTY_ARRAY;

  return selectPayfactorValues(state, payFactorHashId, null);
};

export const selectPayfactorMappings = (state, payFactorHashId = null) => {
  const resolvedHashId = payFactorHashId || state.payfactors.activePayfactor?.hash_id;
  if (!resolvedHashId) return EMPTY_ARRAY;
  return state.payfactors.mappingsByHash[resolvedHashId] || EMPTY_ARRAY;
};

export const selectPayfactorMappingGroupMessages = (state, payFactorHashId = null) => {
  const resolvedHashId = payFactorHashId || state.payfactors.activePayfactor?.hash_id;
  if (!resolvedHashId) return EMPTY_ARRAY;
  return state.payfactors.mappingGroupMessagesByHash[resolvedHashId] || EMPTY_ARRAY;
};

export const selectGroupSelections = (state, payFactorHashId = null, moduleKey = null) => {
  const resolvedHashId = payFactorHashId || state.payfactors.activePayfactor?.hash_id;
  if (!resolvedHashId) return EMPTY_ARRAY;
  return state.payfactors.groupSelectionsByKey[buildScopedCacheKey(resolvedHashId, moduleKey)] || EMPTY_ARRAY;
};

export const selectGlobalPayfactorValues = (state) => state.payfactors.globalValuesByKey?.[buildGlobalValueCacheKey()] || EMPTY_ARRAY;
export const selectGlobalPayfactorMappings = (state) => state.payfactors.globalMappings || EMPTY_ARRAY;
export const selectGlobalPayfactorMappingGroupMessages = (state) => state.payfactors.globalMappingGroupMessages || EMPTY_ARRAY;
export const selectGlobalGroupSelections = (state) => state.payfactors.globalGroupSelections || EMPTY_ARRAY;
export const selectGlobalGradeSelections = (state) => state.payfactors.globalGradeSelections || EMPTY_ARRAY;

export {
  fetchPayfactors,
  fetchActivePayfactor,
  fetchPayfactorValues,
  fetchCurrentPayfactorValues,
  fetchPayfactorMappings,
  fetchGlobalPayfactorValues,
  fetchGlobalPayfactorMappings,
  fetchGroupSelections,
  updateGroupSelections,
  fetchGlobalGroupSelections,
  updateGlobalGroupSelections,
  fetchGlobalGradeSelections,
  updateGlobalGradeSelections,
};

export default payfactorSlice.reducer;
